/**
 * Cameroon Land Market - Automatic Translation Engine
 * 
 * Provides automated bilingual English & French translation for user-submitted property adverts:
 * 1. Automatic source language detection (EN vs FR).
 * 2. Protected Tokenizer for non-translatable entities (Phone numbers, emails, prices,
 *    Titre Foncier numbers, parcel IDs, GPS coordinates, URLs).
 * 3. Controlled Cameroonian real estate terminology dictionary.
 * 4. Resilient fallback & idempotency (never loses seller content, preserves human edits).
 */

import type { LocalizedText, TranslationStatus, TranslationType } from '../types.ts';
import type { WordPressBlock } from '../types/contentPublisher.ts';

export const PROTECTED_PATTERNS = {
  // HTML markup & tags (preserves formatting and tag attributes during article translation)
  tag: /<[^>]+>/g,
  // Cameroon Phone numbers (+237 or local 6xx/2xx)
  phone: /(?:\+?237\s*)?(?:6[5-9][0-9](?:[\s.-]?[0-9]{2}){3}|2[2-4][0-9](?:[\s.-]?[0-9]{2}){3}|6[235-9][0-9]{7})/g,
  // Email addresses
  email: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
  // Prices and currencies (must include a leading or trailing currency code/symbol so plain areas like "1,393 sqm" are not matched as prices)
  price: /(?:(?:XAF|FCFA|CFA|\$|€|£)\s*[0-9]{1,3}(?:[.,\s][0-9]{3})*(?:\s*(?:XAF|FCFA|CFA|francs?|FCFA\/m²|XAF\/m²|million[s]?|milliard[s]?))?|[0-9]{1,3}(?:[.,\s][0-9]{3})*\s*(?:XAF|FCFA|CFA|francs?|FCFA\/m²|XAF\/m²|million[s]?|milliard[s]?))/gi,
  // Titre Foncier numbers & cadastral references (must contain at least one digit)
  titreFoncier: /(?:Titre\s+Foncier|Land\s+Title|TF)\s*(?:N[°o.]?\s*)?(?=[A-Z0-9\/-]*[0-9])[A-Z0-9\/-]+/gi,
  // Parcel and Lot numbers (must contain at least one digit)
  parcel: /(?:Lot|Parcelle|Plot|Parcel|Bloc|Block)\s*(?:N[°o.]?\s*)?(?=[A-Z0-9-]*[0-9])[A-Z0-9-]+/gi,
  // Beacon references
  beacon: /\b[B][0-9]+(?:[\s,-]+[B][0-9]+)*\b/g,
  // GPS Coordinates
  coordinates: /[-+]?[0-9]{1,2}\.[0-9]{4,8}\s*,\s*[-+]?[0-9]{1,3}\.[0-9]{4,8}/g,
  // Web URLs
  url: /https?:\/\/[^\s]+/gi,
};

/**
 * Common French stop words and characteristic linguistic patterns
 */
const FR_INDICATORS = [
  'le', 'la', 'les', 'un', 'une', 'des', 'du', 'de', 'au', 'aux', 'dans',
  'sur', 'pour', 'avec', 'sans', 'sous', 'terrain', 'titré', 'titre', 'foncier',
  'maison', 'chambre', 'chambres', 'salon', 'douche', 'cuisine', 'vendre', 'louer',
  'à', 'sécurisé', 'borne', 'borné', 'bastos', 'bonapriso', 'quartier', 'superficie',
  'pièce', 'pièces', 'immeuble', 'emplacement', 'route', 'goudron'
];

/**
 * Common English stop words and characteristic linguistic patterns
 */
const EN_INDICATORS = [
  'the', 'a', 'an', 'and', 'or', 'in', 'on', 'at', 'for', 'with', 'without',
  'land', 'titled', 'title', 'deed', 'plot', 'plots', 'house', 'bedroom', 'bedrooms',
  'living', 'room', 'bath', 'baths', 'bathroom', 'kitchen', 'sale', 'rent',
  'secured', 'fenced', 'surveyed', 'quarter', 'area', 'sqm', 'commercial',
  'building', 'location', 'road', 'tarred', 'modern', 'spacious'
];

export const CAMEROON_PLACES = [
  'Douala', 'Yaoundé', 'Yaounde', 'Buea', 'Kribi', 'Limbe', 'Bafoussam', 'Bamenda',
  'Garoua', 'Maroua', 'Bertoua', 'Ebolowa', 'Ngaoundéré', 'Ngaoundere', 'Kumba', 'Edéa', 'Edea',
  'Foumban', 'Dschang', 'Mbalmayo', 'Nkongsamba', 'Bonapriso', 'Bastos', 'Molyko', 'Bonamoussadi',
  'Makepe', 'Akwa', 'Deido', 'Odza', 'Santa Barbara', 'Omnisports', 'Biyem-Assi', 'Mendong',
  'Nsimeyong', 'Bokova', 'Muea', 'Mutengene', 'Tiko', 'Bonaberi', 'Logbessou', 'Yassa',
  'Kotto', 'Denver', 'Bali', 'Ndogbong', 'Bépanda', 'Bepanda', 'New Bell',
  'Sandpit', 'Bukwai', 'Great Soppo', 'Small Soppo', 'Bonduma', 'Federal Quarters',
  'Mile 16', 'Mile 17', 'Mile 18', 'Logbadjeck', 'Dibamba', 'Ebombé', 'Ebombe',
  'Japoma', 'Pk14', 'PK14', 'Ngousso', 'Mvan', 'Nkolfoulou', 'Nkolbisson', 'Logpom'
];

/**
 * Normalizes stylized Unicode Mathematical Alphanumeric Symbols (e.g. 𝟓 𝐇𝐄𝐂𝐓𝐀𝐑𝐄𝐒, 𝐓𝐢𝐭𝐫é)
 * into standard ASCII letters and digits while preserving emojis and French accents.
 * Also strips orphaned UTF-16 surrogate halves and repairs known area truncation typos.
 */
export function normalizeStylizedUnicode(text: string): string {
  if (!text) return '';
  return text
    .replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, '')
    .replace(/[\u{1D400}-\u{1D7FF}]/gu, (ch) => ch.normalize('NFKD'))
    .replace(/(^|\s),393\s*sqm\b/gi, '$11,393 sqm');
}

/**
 * Cleans up residual French real-estate terms or casing artifacts in English translations
 */
export function cleanEnglishTranslation(text: string): string {
  if (!text) return '';
  return normalizeStylizedUnicode(text)
    .replace(/\bTitled\s+loti\s+land\b/gi, 'Titled subdivided land')
    .replace(/\bloti\s+land\b/gi, 'subdivided land')
    .replace(/\bTitled,\s*Loti,\s*Morcelable\b/gi, 'Titled, Subdivided, Subdivisible')
    .replace(/\bALLotments\b/g, 'ALLOTMENTS')
    .replace(/\bAllotments\b/g, 'Subdivided Plots')
    .replace(/\bloti[s]?\b/gi, 'subdivided')
    .replace(/\bmorcelable[s]?\b/gi, 'subdivisible');
}

/**
 * Cleans up neighborhood mistranslations or truncated area numbers in French translations
 */
export function cleanFrenchTranslation(text: string): string {
  if (!text) return '';
  return normalizeStylizedUnicode(text)
    .replace(/\bSABLE,\s*BUEA\b/g, 'SANDPIT, BUEA')
    .replace(/\bSable,\s*Buea\b/gi, 'Sandpit, Buea')
    .replace(/\bTerrain\s+de\s+393\s*m²\b/gi, 'Terrain de 1 393 m²');
}

/**
 * Detects whether the input text is predominantly French or English.
 */
export function detectLanguage(text: string): 'en' | 'fr' {
  if (!text || !text.trim()) return 'en';
  const normalized = normalizeStylizedUnicode(text);
  const clean = normalized.toLowerCase();
  const words = clean.match(/[\p{L}]+/gu) || [];
  if (words.length === 0) return 'en';

  let frScore = 0;
  let enScore = 0;

  // Accents specific to French
  if (/[éèêëàâîïôûùç]/i.test(normalized)) {
    frScore += 5;
  }

  for (const word of words) {
    if (FR_INDICATORS.includes(word)) frScore++;
    if (EN_INDICATORS.includes(word)) enScore++;
  }

  return frScore >= enScore ? 'fr' : 'en';
}

/**
 * Replaces non-translatable sensitive entities (phones, emails, prices, IDs) with unique tokens.
 * Returns the tokenized text and a restorer function.
 */
export function protectEntities(text: string): { protectedText: string; restore: (translatedText: string) => string } {
  if (!text) return { protectedText: '', restore: (t) => t };

  const tokens = new Map<string, string>();
  let counter = 0;
  let current = text;

  const replaceWithToken = (regex: RegExp, prefix: string) => {
    current = current.replace(regex, (match) => {
      const token = `__CLM_${prefix}_${counter++}__`;
      tokens.set(token, match);
      return token;
    });
  };

  replaceWithToken(PROTECTED_PATTERNS.tag, 'TAG');
  replaceWithToken(PROTECTED_PATTERNS.url, 'URL');
  replaceWithToken(PROTECTED_PATTERNS.email, 'EMAIL');
  replaceWithToken(PROTECTED_PATTERNS.phone, 'PHONE');
  replaceWithToken(PROTECTED_PATTERNS.coordinates, 'COORD');
  replaceWithToken(PROTECTED_PATTERNS.titreFoncier, 'TF');
  replaceWithToken(PROTECTED_PATTERNS.parcel, 'PARCEL');
  replaceWithToken(PROTECTED_PATTERNS.price, 'PRICE');

  // Protect Cameroonian place names (e.g. Sandpit, Bukwai, Buea) from literal translation
  for (const place of CAMEROON_PLACES) {
    const placeRegex = new RegExp(`\\b${place}\\b`, 'gi');
    replaceWithToken(placeRegex, 'PLACE');
  }

  const restore = (translatedText: string): string => {
    let restored = translatedText;
    for (const [token, original] of tokens.entries()) {
      const looseRegex = new RegExp(token.replace(/_/g, '[_\\s]?'), 'gi');
      restored = restored.replace(looseRegex, original);
    }
    return restored;
  };

  return { protectedText: current, restore };
}

/**
 * Unicode-aware phrase replacer that respects word boundaries even for accented strings
 */
function replacePhrase(text: string, pattern: string, replacement: string): string {
  const regex = new RegExp(`(^|[^\\p{L}\\p{N}_])${pattern}(?=[^\\p{L}\\p{N}_]|$)`, 'giu');
  return text.replace(regex, `$1${replacement}`);
}

/**
 * High-accuracy Cameroon French-to-English translation
 */
export function translateFrToEn(text: string): string {
  let res = normalizeStylizedUnicode(text);

  // Real estate actions & types
  res = replacePhrase(res, 'terrains? titr[ée]s? et lotis?', 'titled and subdivided land');
  res = replacePhrase(res, 'terrain titr[ée] loti', 'titled subdivided land');
  res = replacePhrase(res, 'terrain titr[ée]', 'titled land');
  res = replacePhrase(res, 'parcelle titr[ée]e', 'titled plot');
  res = replacePhrase(res, 'terrain non titr[ée]', 'non-titled land');
  res = replacePhrase(res, 'terrains titr[ée]s', 'titled lands');
  res = replacePhrase(res, 'parcelles titr[ée]es', 'titled plots');
  res = replacePhrase(res, 'titr[ée],\\s*loti,\\s*morcelable', 'titled, subdivided, subdivisible');
  res = replacePhrase(res, 'loti en blocs?', 'subdivided into blocks');
  res = replacePhrase(res, 'lotis?', 'subdivided');
  res = replacePhrase(res, 'morcelables?', 'subdivisible');
  res = replacePhrase(res, 'maison moderne', 'modern house');
  res = replacePhrase(res, 'villa moderne', 'modern villa');
  res = replacePhrase(res, 'villa de standing', 'luxury villa');
  res = replacePhrase(res, 'haut standing', 'high-standing luxury');
  res = replacePhrase(res, 'appartement meubl[ée]', 'furnished apartment');
  res = replacePhrase(res, 'studio meubl[ée]', 'furnished studio');
  res = replacePhrase(res, 'immeuble commercial', 'commercial building');
  res = replacePhrase(res, 'local commercial', 'commercial space');
  res = replacePhrase(res, 'locaux commerciaux', 'commercial spaces');
  res = replacePhrase(res, 'domaine agricole', 'agricultural estate / farmland');
  res = replacePhrase(res, 'terre fertile', 'fertile land');

  // Transactions
  res = replacePhrase(res, '[àa] vendre', 'for sale');
  res = replacePhrase(res, 'to vendre', 'for sale');
  res = replacePhrase(res, '[àa] louer', 'for rent');

  // Cameroon Town & Place Prepositions: "à Buea" / "à Logbadjeck" -> "in Buea" / "in Logbadjeck"
  res = res.replace(/(^|[^\p{L}\p{N}_])(?:[àa]|to)\s+(Buea|Douala|Yaound[ée]|Kribi|Limbe|Bafoussam|Bamenda|Garoua|Maroua|Ngaound[ée]r[ée]|Bertoua|Ebolowa|Kumba|Ed[ée]a|Dschang|Foumban|Bafia|Sangm[ée]lima|Mbalmayo|Tiko|Menji|Fontem|Bastos|Bonapriso|Akwa|Omnisports|Bonamoussadi|Makepe|Santa Barbara|Mvan|Odza|Ngousso|Kotto|Logpom|Logbadjeck|Ebomb[ée]|Dibamba|Nkolfoulou|Yassa)/giu, '$1in $2');

  // Documents & Verification
  res = replacePhrase(res, 'documents? [àa] jour', 'up-to-date documents');
  res = replacePhrase(res, 'documents? s[ée]curis[ée]s? et v[ée]rifiables?', 'secure and verifiable documents');
  res = replacePhrase(res, 'documents? v[ée]rifiables?', 'verifiable documents');
  res = replacePhrase(res, 'titre foncier disponible', 'land title deed available');
  res = replacePhrase(res, 'titre foncier', 'land title');
  res = replacePhrase(res, 'bornes de bornage', 'boundary beacons');
  res = replacePhrase(res, 'bornes', 'boundary beacons');
  res = replacePhrase(res, 'born[ée]', 'demarcated with beacons');
  res = replacePhrase(res, 'plan de bornage', 'survey plan');
  res = replacePhrase(res, 'certificat de propri[ée]t[ée]', 'certificate of ownership');
  res = replacePhrase(res, 'mutation totale', 'direct ownership transfer (mutation totale)');
  res = replacePhrase(res, 'documents? disponible[s]?', 'documents available');

  // Common phrases in Cameroon land descriptions
  res = replacePhrase(res, 'l[\'’]opportunit[ée] que vous attendiez', 'the opportunity you have been waiting for');
  res = replacePhrase(res, 'vous achetez la taille que vous voulez', 'buy the plot size you want');
  res = replacePhrase(res, 'prix exceptionnel', 'exceptional price');
  res = replacePhrase(res, 'pourquoi choisir ce site', 'why choose this site');
  res = replacePhrase(res, 'pr[êe]t [àa] b[âa]tir', 'ready to build');
  res = replacePhrase(res, 'centre[\\s-]ville de', 'city center of');
  res = replacePhrase(res, 'centre[\\s-]ville', 'city center');
  res = replacePhrase(res, 'plus pr[ée]cis[ée]ment [àa]', 'more precisely in');
  res = replacePhrase(res, 'dans les environs d[\'’]', 'in the surroundings of ');
  res = replacePhrase(res, 'le terrain est situ[ée] au quartier', 'the land is located in the neighborhood of');
  res = replacePhrase(res, 'le terrain est situ[ée] [àa]', 'the land is located in');
  res = replacePhrase(res, 'non loin de', 'not far from');
  res = replacePhrase(res, 'en moto du carrefour', 'by motorbike from the junction');
  res = replacePhrase(res, 'visite du site gratuite', 'free site visit');
  res = replacePhrase(res, 'r[ée]servez maintenant', 'book now');
  res = replacePhrase(res, 'localisation du terrain', 'land location');
  res = replacePhrase(res, 'localisation', 'location');
  res = replacePhrase(res, 'situation fonci[èe]re', 'land title status');
  res = replacePhrase(res, 'grande superficie', 'large surface area');

  // Features & Access
  res = replacePhrase(res, 'tr[èe]s bien situ[ée]', 'very well located');
  res = replacePhrase(res, 'bien situ[ée]', 'well situated');
  res = replacePhrase(res, 'acc[èe]s facile', 'easy access');
  res = replacePhrase(res, 'acc[èe]s goudronn[ée]', 'tarred road access');
  res = replacePhrase(res, 'proche de la route', 'close to the main road');
  res = replacePhrase(res, 'proche du goudron', 'close to the tarred road');
  res = replacePhrase(res, 'quartier r[ée]sidentiel', 'residential neighborhood');
  res = replacePhrase(res, 'quartier calme', 'quiet neighborhood');
  res = replacePhrase(res, 'zone s[ée]curis[ée]e', 'secure area');
  res = replacePhrase(res, 'cl[ôo]tur[ée]', 'fenced');
  res = replacePhrase(res, 'cl[ôo]ture s[ée]curis[ée]e', 'secure fence');
  res = replacePhrase(res, 'forage d\'eau', 'water borehole');
  res = replacePhrase(res, 'forage', 'borehole');
  res = replacePhrase(res, 'compteur eneo', 'electricity meter');
  res = replacePhrase(res, 'compteur [ée]lectrique', 'electricity meter');
  res = replacePhrase(res, 'eau et [ée]lectricit[ée] disponible[s]?', 'water and electricity available');
  res = replacePhrase(res, 'raccordement eau', 'water connection');
  res = replacePhrase(res, 'groupe [ée]lectrog[èe]ne', 'backup generator');
  res = replacePhrase(res, 'piscine', 'swimming pool');
  res = replacePhrase(res, 'chambre principale', 'master bedroom');
  res = replacePhrase(res, 'cuisine moderne', 'modern kitchen');
  res = replacePhrase(res, 'grande cour', 'spacious compound');
  res = replacePhrase(res, 'espace parking', 'parking space');
  res = replacePhrase(res, 'visite guid[ée]e', 'guided site visit');
  res = replacePhrase(res, 'prix n[ée]gociable', 'negotiable price');
  res = replacePhrase(res, 'prix l[ée]g[èe]rement n[ée]gociable', 'slightly negotiable price');
  res = replacePhrase(res, 'contactez-nous', 'contact us');
  res = replacePhrase(res, 'veuillez contacter', 'please contact');
  res = replacePhrase(res, 'pour plus d[\'’]informations?', 'for more information');
  res = replacePhrase(res, 'disponible imm[ée]diatement', 'available immediately');
  res = replacePhrase(res, 'superbe parcelle', 'superb plot');
  res = replacePhrase(res, 'superbe', 'superb');
  res = replacePhrase(res, 'magnifique', 'magnificent');
  res = replacePhrase(res, 'parcelle', 'plot');
  res = replacePhrase(res, 'parcelles', 'plots');
  res = replacePhrase(res, 'terrain', 'land');
  res = replacePhrase(res, 'terrains', 'lands');
  res = replacePhrase(res, 'maison', 'house');
  res = replacePhrase(res, 'maisons', 'houses');
  res = replacePhrase(res, 'appartement', 'apartment');
  res = replacePhrase(res, 'appartements', 'apartments');
  res = replacePhrase(res, 'avec', 'with');
  res = replacePhrase(res, 'sans', 'without');
  res = replacePhrase(res, 'ou', 'or');
  res = replacePhrase(res, 'et', 'and');
  res = replacePhrase(res, 'prix', 'price');
  res = replacePhrase(res, 'n[ée]gociable', 'negotiable');

  // Rooms & measurements
  res = res.replace(/(^|[^\p{L}\p{N}_])([0-9]+)\s*chambre[s]?\s+avec\s+douche[s]?/giu, '$1$2 ensuite bedroom(s)');
  res = res.replace(/(^|[^\p{L}\p{N}_])([0-9]+)\s*chambre[s]?/giu, '$1$2 bedroom(s)');
  res = res.replace(/(^|[^\p{L}\p{N}_])([0-9]+)\s*douche[s]?/giu, '$1$2 bathroom(s)');
  res = res.replace(/(^|[^\p{L}\p{N}_])([0-9]+)\s*salon[s]?/giu, '$1$2 living room(s)');
  res = res.replace(/(^|[^\p{L}\p{N}_])([0-9]+)\s*cuisine[s]?/giu, '$1$2 kitchen(s)');
  res = replacePhrase(res, 'superficie de', 'surface area of');
  res = replacePhrase(res, 'superficie', 'surface area');
  res = res.replace(/([0-9]+)\s*m\s*carr[ée]s?/giu, '$1 m²');
  res = replacePhrase(res, 'm\\s*carr[ée]s?', 'm²');
  res = replacePhrase(res, 'm[²2]', 'sqm');
  res = replacePhrase(res, 'mètres carrés', 'square meters');

  return res.charAt(0).toUpperCase() + res.slice(1);
}

/**
 * High-accuracy Cameroon English-to-French translation
 */
export function translateEnToFr(text: string): string {
  let res = text;

  // Real estate actions & types
  res = replacePhrase(res, 'titled land', 'terrain titré');
  res = replacePhrase(res, 'titled plot', 'parcelle titrée');
  res = replacePhrase(res, 'non-titled land', 'terrain non titré');
  res = replacePhrase(res, 'titled plots', 'parcelles titrées');
  res = replacePhrase(res, 'modern house', 'maison moderne');
  res = replacePhrase(res, 'modern villa', 'villa moderne');
  res = replacePhrase(res, 'luxury villa', 'villa de standing');
  res = replacePhrase(res, 'high-standing luxury', 'haut standing');
  res = replacePhrase(res, 'furnished apartment', 'appartement meublé');
  res = replacePhrase(res, 'furnished studio', 'studio meublé');
  res = replacePhrase(res, 'commercial building', 'immeuble commercial');
  res = replacePhrase(res, 'commercial space', 'local commercial');
  res = replacePhrase(res, 'commercial spaces', 'locaux commerciaux');
  res = replacePhrase(res, 'agricultural estate', 'domaine agricole');
  res = replacePhrase(res, 'fertile land', 'terre fertile');

  // Transactions
  res = replacePhrase(res, 'for sale', 'à vendre');
  res = replacePhrase(res, 'for rent', 'à louer');

  // Cameroon Town Prepositions: "in Buea" -> "à Buea"
  res = res.replace(/(^|[^\p{L}\p{N}_])in\s+(Buea|Douala|Yaound[ée]|Kribi|Limbe|Bafoussam|Bamenda|Garoua|Maroua|Ngaound[ée]r[ée]|Bertoua|Ebolowa|Kumba|Ed[ée]a|Dschang|Foumban|Bafia|Sangm[ée]lima|Mbalmayo|Tiko|Menji|Fontem|Bastos|Bonapriso|Akwa|Omnisports|Bonamoussadi|Makepe|Santa Barbara|Mvan|Odza|Ngousso|Kotto|Logpom)/giu, '$1à $2');

  // Documents & Verification
  res = replacePhrase(res, 'land title deed available', 'titre foncier disponible');
  res = replacePhrase(res, 'land title available', 'titre foncier disponible');
  res = replacePhrase(res, 'land title', 'titre foncier');
  res = replacePhrase(res, 'boundary beacons', 'bornes de bornage');
  res = replacePhrase(res, 'survey plan', 'plan de bornage');
  res = replacePhrase(res, 'certificate of ownership', 'certificat de propriété');
  res = replacePhrase(res, 'documents available', 'documents disponibles');
  res = replacePhrase(res, 'fenced', 'clôturé');
  res = replacePhrase(res, 'demarcated', 'borné');

  // Features & Access
  res = replacePhrase(res, 'very well located', 'très bien situé');
  res = replacePhrase(res, 'well situated', 'bien situé');
  res = replacePhrase(res, 'well located', 'bien situé');
  res = replacePhrase(res, 'easy access', 'accès facile');
  res = replacePhrase(res, 'tarred road access', 'accès goudronné');
  res = replacePhrase(res, 'close to the main road', 'proche de l\'axe principal');
  res = replacePhrase(res, 'close to the tarred road', 'proche du goudron');
  res = replacePhrase(res, 'residential neighborhood', 'quartier résidentiel');
  res = replacePhrase(res, 'quiet neighborhood', 'quartier calme');
  res = replacePhrase(res, 'secure area', 'zone sécurisée');
  res = replacePhrase(res, 'secure fence', 'clôture sécurisée');
  res = replacePhrase(res, 'water borehole', 'forage d\'eau');
  res = replacePhrase(res, 'borehole', 'forage d\'eau');
  res = replacePhrase(res, 'electricity meter', 'compteur électrique');
  res = replacePhrase(res, 'water and electricity available', 'eau et électricité disponibles');
  res = replacePhrase(res, 'water connection', 'raccordement eau');
  res = replacePhrase(res, 'backup generator', 'groupe électrogène');
  res = replacePhrase(res, 'swimming pool', 'piscine');
  res = replacePhrase(res, 'master bedroom', 'chambre principale');
  res = replacePhrase(res, 'modern kitchen', 'cuisine moderne');
  res = replacePhrase(res, 'spacious compound', 'grande cour');
  res = replacePhrase(res, 'parking space', 'espace parking');
  res = replacePhrase(res, 'guided site visit', 'visite guidée sur le terrain');
  res = replacePhrase(res, 'negotiable price', 'prix négociable');
  res = replacePhrase(res, 'slightly negotiable', 'légèrement négociable');
  res = replacePhrase(res, 'contact us', 'contactez-nous');
  res = replacePhrase(res, 'please contact', 'veuillez contacter');
  res = replacePhrase(res, 'for more information', 'pour plus d\'informations');
  res = replacePhrase(res, 'superb plot', 'superbe parcelle');
  res = replacePhrase(res, 'superb', 'superbe');
  res = replacePhrase(res, 'magnificent', 'magnifique');
  res = replacePhrase(res, 'plot', 'parcelle');
  res = replacePhrase(res, 'plots', 'parcelles');
  res = replacePhrase(res, 'land', 'terrain');
  res = replacePhrase(res, 'lands', 'terrains');
  res = replacePhrase(res, 'house', 'maison');
  res = replacePhrase(res, 'houses', 'maisons');
  res = replacePhrase(res, 'apartment', 'appartement');
  res = replacePhrase(res, 'apartments', 'appartements');
  res = replacePhrase(res, 'with', 'avec');
  res = replacePhrase(res, 'without', 'sans');
  res = replacePhrase(res, 'or', 'ou');
  res = replacePhrase(res, 'and', 'et');
  res = replacePhrase(res, 'price', 'prix');
  res = replacePhrase(res, 'negotiable', 'négociable');

  // Rooms & measurements
  res = res.replace(/(^|[^\p{L}\p{N}_])([0-9]+)\s*bedrooms?/giu, '$1$2 chambres');
  res = res.replace(/(^|[^\p{L}\p{N}_])([0-9]+)\s*bathrooms?/giu, '$1$2 douches');
  res = res.replace(/(^|[^\p{L}\p{N}_])([0-9]+)\s*living rooms?/giu, '$1$2 salon');
  res = res.replace(/(^|[^\p{L}\p{N}_])([0-9]+)\s*kitchens?/giu, '$1$2 cuisine');
  res = replacePhrase(res, 'surface area of', 'superficie de');
  res = replacePhrase(res, 'surface area', 'superficie');
  res = replacePhrase(res, 'sqm', 'm²');
  res = replacePhrase(res, 'square meters', 'mètres carrés');

  return res;
}

/**
 * Translates text between French and English using the neural translation service with resilient fallback.
 * Preserves non-translatable entities (phones, emails, prices, coords, TF numbers).
 */
export async function translateTextAsync(
  text: string,
  sourceLang: 'en' | 'fr'
): Promise<{ translated: string; targetLang: 'en' | 'fr' }> {
  if (!text || !text.trim()) {
    return { translated: '', targetLang: sourceLang === 'fr' ? 'en' : 'fr' };
  }

  const normalizedInput = normalizeStylizedUnicode(text);
  const targetLang = sourceLang === 'fr' ? 'en' : 'fr';
  const { protectedText, restore } = protectEntities(normalizedInput);

  let rawTranslated = '';

  // 1. Attempt High-Speed Online Translation API
  try {
    const url = new URL('https://translate.googleapis.com/translate_a/single');
    url.searchParams.set('client', 'gtx');
    url.searchParams.set('sl', sourceLang);
    url.searchParams.set('tl', targetLang);
    url.searchParams.set('dt', 't');
    url.searchParams.set('q', protectedText);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 7000);
    const res = await fetch(url.toString(), { signal: controller.signal });
    clearTimeout(timeoutId);

    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && Array.isArray(data[0])) {
        const joined = data[0].map((s: any) => (s && s[0]) || '').join('');
        if (joined && joined.trim()) {
          rawTranslated = joined;
        }
      }
    }
  } catch (err) {
    console.warn('[TRANSLATION ENGINE NOTICE] Online translation API error, fallback active:', err);
  }

  // 2. Fallback to Cameroon Real Estate Lexicon dictionary if online translation didn't succeed
  if (!rawTranslated || !rawTranslated.trim()) {
    if (sourceLang === 'fr') {
      rawTranslated = translateFrToEn(protectedText);
    } else {
      rawTranslated = translateEnToFr(protectedText);
    }
  }

  // 3. Restore protected entities (phones, prices, coordinates, TF numbers)
  let finalized = restore(rawTranslated);

  // 4. Polish Cameroon real-estate legal terms
  if (targetLang === 'en') {
    finalized = cleanEnglishTranslation(finalized);
  } else {
    finalized = cleanFrenchTranslation(finalized).replace(/\bsqm\b/gi, 'm²');
  }

  const capitalized = finalized ? finalized.charAt(0).toUpperCase() + finalized.slice(1) : '';
  return { translated: capitalized, targetLang };
}

/**
 * Synchronous translation fallback using local dictionary
 */
export function translateText(text: string, sourceLang: 'en' | 'fr'): { translated: string; targetLang: 'en' | 'fr' } {
  if (!text || !text.trim()) {
    return { translated: '', targetLang: sourceLang === 'fr' ? 'en' : 'fr' };
  }

  const targetLang = sourceLang === 'fr' ? 'en' : 'fr';
  const { protectedText, restore } = protectEntities(text);

  let rawTranslated = '';
  if (sourceLang === 'fr') {
    rawTranslated = translateFrToEn(protectedText);
  } else {
    rawTranslated = translateEnToFr(protectedText);
  }

  // Restore protected entities
  const finalized = restore(rawTranslated);
  const capitalized = finalized ? finalized.charAt(0).toUpperCase() + finalized.slice(1) : '';
  return { translated: capitalized, targetLang };
}

export interface TranslateListingParams {
  title: string;
  description?: string;
  originalLanguage?: 'en' | 'fr';
  existingTitle?: LocalizedText;
  existingDescription?: LocalizedText;
  forceRegenerate?: boolean;
}

export interface TranslateListingResult {
  localizedTitle: LocalizedText;
  localizedDescription: LocalizedText;
  detectedLanguage: 'en' | 'fr';
}

/**
 * Automatically processes a listing's title and description for dual-locale storage.
 * - Detects source language.
 * - Preserves existing human edits if present.
 * - Idempotently skips unchanged content (detects and fixes faulty identical translations).
 * - Fails safely: never throws or blocks publishing.
 */
export async function translateListingContent(
  params: TranslateListingParams
): Promise<TranslateListingResult> {
  const { title = '', description = '', existingTitle, existingDescription, forceRegenerate = false } = params;

  // 1. Detect Source Language
  const detected = params.originalLanguage || detectLanguage(`${title} ${description}`);
  const now = new Date().toISOString();

  // Helper to build a localized field
  const buildField = async (
    originalText: string,
    existing?: LocalizedText
  ): Promise<LocalizedText> => {
    // If existing translation was human-edited or professionally reviewed, preserve it unless forced
    if (
      !forceRegenerate &&
      existing &&
      (existing.translationType === 'human_edited' || existing.translationType === 'professionally_reviewed')
    ) {
      return {
        ...existing,
        [detected]: originalText,
      };
    }

    // Detect faulty previous translations where English and French were saved as the exact identical text
    const isFaultyIdentical =
      existing &&
      existing.en &&
      existing.fr &&
      existing.en.trim().toLowerCase() === existing.fr.trim().toLowerCase() &&
      existing.translationType === 'machine_translated';

    // If text hasn't changed and has valid non-identical translation, reuse existing translation
    if (!forceRegenerate && !isFaultyIdentical && existing && existing[detected] === originalText && existing.translationStatus === 'completed') {
      return existing;
    }

    try {
      const { translated } = await translateTextAsync(originalText, detected);
      return {
        originalLanguage: detected,
        en: detected === 'en' ? originalText : translated,
        fr: detected === 'fr' ? originalText : translated,
        translationStatus: 'completed',
        translationType: 'machine_translated',
        translatedAt: now,
        translationVersion: (existing?.translationVersion || 0) + 1,
      };
    } catch (err) {
      console.warn('[TRANSLATION ENGINE NOTICE] Translation fallback activated:', err);
      const syncFallback = translateText(originalText, detected);
      return {
        originalLanguage: detected,
        en: detected === 'en' ? originalText : syncFallback.translated,
        fr: detected === 'fr' ? originalText : syncFallback.translated,
        translationStatus: 'completed',
        translationType: 'machine_translated',
        translatedAt: now,
        translationVersion: (existing?.translationVersion || 0) + 1,
      };
    }
  };

  const localizedTitle = await buildField(title.trim(), existingTitle);
  const localizedDescription = await buildField(description.trim(), existingDescription);

  return {
    localizedTitle,
    localizedDescription,
    detectedLanguage: detected,
  };
}

export interface PostTranslationInput {
  title?: string;
  excerpt?: string;
  content?: string;
  sourceLang?: 'en' | 'fr';
}

export interface PostTranslationResult {
  title_en: string;
  title_fr: string;
  excerpt_en: string;
  excerpt_fr: string;
  content_en: string;
  content_fr: string;
  detectedLang: 'en' | 'fr';
}

/**
 * Automatically translates a news post, guide, or publisher article between English and French.
 * - Detects source language
 * - Preserves HTML tags and formatting
 * - Protects legal entities, phone numbers, and place names
 * - Falls back reliably to Cameroon local dictionary if offline
 */
export async function translatePostOrArticleAsync(
  input: PostTranslationInput
): Promise<PostTranslationResult> {
  const rawTitle = (input.title || '').trim();
  const rawExcerpt = (input.excerpt || '').trim();
  const rawContent = (input.content || '').trim();

  const detected: 'en' | 'fr' =
    input.sourceLang || detectLanguage(`${rawTitle} ${rawExcerpt} ${rawContent.slice(0, 500)}`);

  const translateField = async (text: string): Promise<{ en: string; fr: string }> => {
    if (!text) return { en: '', fr: '' };
    if (detected === 'en') {
      try {
        const { translated } = await translateTextAsync(text, 'en');
        return { en: text, fr: translated || translateEnToFr(text) };
      } catch {
        return { en: text, fr: translateEnToFr(text) };
      }
    } else {
      try {
        const { translated } = await translateTextAsync(text, 'fr');
        return { en: translated || translateFrToEn(text), fr: text };
      } catch {
        return { en: translateFrToEn(text), fr: text };
      }
    }
  };

  const titleRes = await translateField(rawTitle);
  const excerptRes = await translateField(rawExcerpt);
  const contentRes = await translateField(rawContent);

  return {
    title_en: titleRes.en,
    title_fr: titleRes.fr,
    excerpt_en: excerptRes.en,
    excerpt_fr: excerptRes.fr,
    content_en: contentRes.en,
    content_fr: contentRes.fr,
    detectedLang: detected,
  };
}

/**
 * Translates Gutenberg editor blocks to ensure full dual-language support for articles and guides.
 */
export async function translateGutenbergBlocksAsync(
  blocks: WordPressBlock[],
  targetLang: 'en' | 'fr'
): Promise<WordPressBlock[]> {
  const sourceLang: 'en' | 'fr' = targetLang === 'fr' ? 'en' : 'fr';

  const translateStr = async (str?: string): Promise<string> => {
    if (!str || !str.trim()) return '';
    try {
      const { translated } = await translateTextAsync(str, sourceLang);
      return translated || (sourceLang === 'en' ? translateEnToFr(str) : translateFrToEn(str));
    } catch {
      return sourceLang === 'en' ? translateEnToFr(str) : translateFrToEn(str);
    }
  };

  const translatedBlocks: WordPressBlock[] = [];

  for (const block of blocks) {
    const b = { ...block };

    if (b.content) {
      const transContent = await translateStr(b.content);
      if (targetLang === 'fr') {
        b.content_fr = transContent;
      }
      b.content = transContent;
    }

    if (b.caption) {
      const transCaption = await translateStr(b.caption);
      if (targetLang === 'fr') b.caption_fr = transCaption;
      b.caption = transCaption;
    }

    if (b.alt) {
      const transAlt = await translateStr(b.alt);
      if (targetLang === 'fr') b.alt_fr = transAlt;
      b.alt = transAlt;
    }

    if (b.authorQuote) {
      b.authorQuote = await translateStr(b.authorQuote);
    }

    if (b.items && b.items.length > 0) {
      const transItems: string[] = [];
      for (const item of b.items) {
        transItems.push(await translateStr(item));
      }
      if (targetLang === 'fr') b.items_fr = transItems;
      b.items = transItems;
    }

    if (b.tableHeaders && b.tableHeaders.length > 0) {
      const transHeaders: string[] = [];
      for (const h of b.tableHeaders) {
        transHeaders.push(await translateStr(h));
      }
      if (targetLang === 'fr') b.tableHeaders_fr = transHeaders;
      b.tableHeaders = transHeaders;
    }

    if (b.tableRows && b.tableRows.length > 0) {
      const transRows: string[][] = [];
      for (const row of b.tableRows) {
        const transRow: string[] = [];
        for (const cell of row) {
          transRow.push(await translateStr(cell));
        }
        transRows.push(transRow);
      }
      if (targetLang === 'fr') b.tableRows_fr = transRows;
      b.tableRows = transRows;
    }

    if (b.faqItems && b.faqItems.length > 0) {
      const transFaqs = [];
      for (const faq of b.faqItems) {
        const q = await translateStr(faq.question);
        const a = await translateStr(faq.answer);
        transFaqs.push({
          question: q,
          question_fr: targetLang === 'fr' ? q : faq.question_fr,
          answer: a,
          answer_fr: targetLang === 'fr' ? a : faq.answer_fr,
        });
      }
      b.faqItems = transFaqs;
    }

    translatedBlocks.push(b);
  }

  return translatedBlocks;
}

